import { useFirstMountState, useMountEffect, useSyncedRef } from "@react-hookz/web";
import Cookies from "js-cookie";
import { useCallback, useState } from "react";

export type UseCookieValueOption = Cookies.CookieAttributes & {
  initializeWithValue?: boolean;
};

export const useCookieValue = (
  cookieName: string,
  options?: UseCookieValueOption,
): [null | string, (newValue: string, options?: Cookies.CookieAttributes) => void, () => void] => {
  const latestOptions = useSyncedRef(options);

  const isFirstMount = useFirstMountState();
  const [value, setValue] = useState<null | string>(() => {
    if (isFirstMount && options?.initializeWithValue) return Cookies.get(cookieName) ?? null;
    return null;
  });

  const updateCookie = useCallback(
    (newValue: string) => {
      Cookies.set(cookieName, newValue, latestOptions.current);
      setValue(newValue);
    },
    [cookieName, latestOptions],
  );

  const deleteCookie = useCallback(() => {
    Cookies.remove(cookieName);
    setValue(null);
  }, [cookieName]);

  useMountEffect(() => {
    const cookieValue = Cookies.get(cookieName);
    if (value || !cookieValue) return;
    setValue(cookieValue);
  });

  // eslint-disable-next-line @shopify/react-hooks-strict-return
  return [value, updateCookie, deleteCookie];
};
